import { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../common/Header/Header';
import {
  ButtonLabel,
  Buttons,
  ButtonsSeparator,
  Page,
  TableWrapper,
} from '../../common/TableWithButtonsRightLayout/TableWithButtonsRightLayout.styles';
import Button from '../../components/Button/Button';
import { Variant } from '../../components/Button/Button.types';
import Input from '../../components/Input/Input';
import Modal from '../../components/Modal/Modal';
import { showToast } from '../../components/Toast/Toast';
import { ToastTypes } from '../../components/Toast/Toast.types';
import TreeView from '../../components/TreeView/TreeView';
import { BuildRequestBodyParams } from '../../components/TreeView/TreeView.types';
import useTreeViewRowStates from '../../components/TreeView/useTreeViewRowStates';
import { useGetSubRecipeBases } from '../../services/base/base.service';
import { useGetClasses } from '../../services/class/class.service';
import { useGetSubRecipeOptions } from '../../services/option/option.service';
import { useGetSubRecipeProducts } from '../../services/product/product.service';
import { useGetSubRecipeSizes } from '../../services/size/size.service';
import { useSpecsInSubrecipe } from '../../services/spec/spec.service';
import { useSubrecipesInSpec } from '../../services/subrecipe/subrecipe.service';
import { genericErrorShowToastOptions } from '../../utils/useGenericErrorMessage/useGenericErrorMessage';
import { StyledButtons, StyledMessage } from '../Recipes/Recipes.styles';
import { CLASS_STATUS } from '../../constants/ApiEnums';

export const navigationRoutes = {
  copy: '/optionrecipes/copy',
  changeUsage: '/optionrecipes/changeusage',
  delete: '/optionrecipes/delete',
};

const OptionRecipes: FC = () => {
  const { data: classesData, error: classesError } = useGetClasses(Number(CLASS_STATUS.ACTIVE));
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [specNo, setSpecNo] = useState('');
  const [specNoError, setSpecNoError] = useState('');
  const [fetchEnabled, setFetchEnabled] = useState(false);
  const { data: recipeData, error, remove } = useSubrecipesInSpec({ specNo }, fetchEnabled);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const handleModalOpen = () => {
    setModalOpen(true);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 0);
  };

  const closeModal = () => {
    setFetchEnabled(false);
    setSpecNo('');
    setModalOpen(false);
    setDeleteModalOpen(false);
  };

  const openModal = handleModalOpen;
  const openDeleteModal = () => setDeleteModalOpen(true);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (specNoError) setSpecNoError('');
    setSpecNo(e.target.value);
  };

  const navigateToRecipe = () => {
    setFetchEnabled(true);
  };

  const navigateToPage = (endpoint: string) => {
    navigate(endpoint);
  }

  const navigateToCopy = () => {
    navigateToPage(navigationRoutes.copy);
  }

  const navigateToChangeUsage = () => {
    navigateToPage(navigationRoutes.changeUsage);
  }

  const navigateToDelete = () => {
    navigateToPage(navigationRoutes.delete);
  }

  useEffect(() => {
    if (error) {
      setSpecNoError('Option recipe not found');
      setFetchEnabled(false);
      setSpecNo('');
      remove();
    }
  }, [error]);

  useEffect(() => {
    if (recipeData?.data && specNo !== '' && fetchEnabled) {
      navigate(`/optionrecipes/${specNo}`);
    }
    return () => remove();
  }, [recipeData, specNo, fetchEnabled]);

  useEffect(() => {
    if (classesError)
      showToast({
        ...genericErrorShowToastOptions,
        label: 'Error fetching classes.',
        id: 'classFetchError',
        toastType: ToastTypes.Error,
      });
  }, [classesError]);

  const dataStructure = [
    {
      fetchChildren: useGetSubRecipeBases,
      idKey: 'classNo',
      buildLabel: (params: BuildRequestBodyParams) => params.row.description,
      buildRequestBody: (params: BuildRequestBodyParams) => {
        return { classNo: params.row.classNo };
      },
    },
    {
      fetchChildren: useGetSubRecipeSizes,
      idKey: 'baseNo',
      buildLabel: (params: BuildRequestBodyParams) => params.row.description,
      buildRequestBody: (params: BuildRequestBodyParams) => {
        return { classNo: params.parentId, baseNo: params.row.baseNo };
      },
    },
    {
      fetchChildren: useGetSubRecipeProducts,
      idKey: 'sizeNo',
      buildLabel: (params: BuildRequestBodyParams) => params.row.description,
      buildRequestBody: (params: BuildRequestBodyParams) => {
        return {
          classNo: params.parentRows?.[0].id,
          baseNo: params.parentId,
          sizeNo: params.row.sizeNo,
        };
      },
    },
    {
      fetchChildren: useGetSubRecipeOptions,
      idKey: 'productNo',
      buildLabel: (params: BuildRequestBodyParams) => params.row.description,
      buildRequestBody: (params: BuildRequestBodyParams) => {
        return {
          classNo: params.parentRows?.[0].id,
          baseNo: params.parentRows?.[1].id,
          sizeNo: params.parentId,
          productNo: params.row.productNo,
        };
      },
    },
    {
      fetchChildren: useSpecsInSubrecipe,
      idKey: 'optionNo',
      buildLabel: (params: BuildRequestBodyParams) => params.row.description,
      buildRequestBody: (params: BuildRequestBodyParams) => {
        return {
          classNo: params.parentRows?.[0].id,
          baseNo: params.parentRows?.[1].id,
          sizeNo: params.parentRows?.[2].id,
          productNo: params.parentId,
          optionNo: params.row.optionNo,
        };
      },
    },
    {
      to: (params: BuildRequestBodyParams) => {
        return {
          navigateTo: `/optionrecipes/${params.row.specNo}`,
          navigateOptions: {
            state: {
              classNo: params.parentRows?.[0].id,
              baseNo: params.parentRows?.[1].id,
              sizeNo: params.parentRows?.[2].id,
              prodNo: params.parentRows?.[3].id,
            },
          },
        };
      },
      idKey: 'specNo',
      buildLabel: (params: BuildRequestBodyParams) => `${params.row.specNo} - ${params.row.description}`,
    },
  ];

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      navigateToRecipe();
    }
  };

  const { expandedRows, handleRowClick, reset } = useTreeViewRowStates(dataStructure, 'selectOptionRecipes');

  return (
    <>
      <Header breadcrumb={{ to: '/tables', label: 'All tables' }} title='Option Recipes' />
      <Page>
        <TableWrapper>
          {classesData && (
            <TreeView
              rows={classesData.data}
              dataStructure={dataStructure}
              expandedRows={expandedRows}
              onRowClick={handleRowClick}
            />
          )}
        </TableWrapper>
        <Buttons>
          <ButtonLabel>Option Recipe</ButtonLabel>
          <Button label='Open' testId='OpenOptionRecipeActionButton' variant={Variant.Primary} onClick={openModal} />
          <Button label='Copy' testId='CopyOptionRecipeActionButton' variant={Variant.Secondary} onClick={navigateToCopy} />
          <Button label='Delete' testId='DeleteOptionRecipeActionButton' variant={Variant.Secondary} onClick={openDeleteModal} />
          <ButtonsSeparator />
          <ButtonLabel>Rawprod Bulk Actions</ButtonLabel>
          {/* <Button label='Add' variant={Variant.Secondary} /> */}
          {/* <Button label='Change' variant={Variant.Secondary} /> */}
          <Button
            label='Change Usage'
            testId='ChangeUsageOptionRecipeActionButton'
            variant={Variant.Secondary}
            onClick={navigateToChangeUsage}
          />
          <Button
            label='Change Raw Products'
            testId='OptionRecipeChangeRawProductsButton'
            variant={Variant.Secondary}
            onClick={() => navigate(`/optionrecipes/change-raw-products`)}
          />
          <ButtonsSeparator />
          <ButtonLabel>View</ButtonLabel>
          <Button label='Reset' testId='ResetViewOptionRecipeActionButton' variant={Variant.Secondary} onClick={reset} />
        </Buttons>
        <Modal id='openOptionRecipeBySpecNoModal' open={modalOpen} onClose={closeModal} title='Open Option Recipe'>
          <div>
            <StyledMessage>Enter the spec number you want to open.</StyledMessage>
            <Input
              label='Spec Number'
              onChange={handleInputChange}
              value={specNo}
              id='openOptionRecipeBySpecNo'
              errorText={specNoError}
              ref={inputRef}
              onKeyDown={handleKeyDown}
            />
            <StyledButtons>
              <Button variant={Variant.Secondary} label='Cancel' onClick={closeModal} />
              <Button label='Open' onClick={navigateToRecipe} disabled={specNo === ''} />
            </StyledButtons>
          </div>
        </Modal>
        <Modal id='openDeleteOptionRecipesModal' open={deleteModalOpen} onClose={closeModal} title='Continue?'>
          <div>
            <StyledMessage>This action will take 30-60 seconds to load.</StyledMessage>
            <StyledButtons>
              <Button label='Cancel' variant={Variant.Secondary} onClick={closeModal} />
              <Button label='Continue' onClick={navigateToDelete} />
            </StyledButtons>
          </div>
        </Modal>
      </Page>
    </>
  );
};

export default OptionRecipes;
